import React, { useEffect, useState } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TablePagination,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffectAsync } from "../reactHelper";
import { getWithExpiry } from "../common/util/localstorage";
import SearchHeader from "../common/components/SearchHeader";
import CollectionActions from "../common/components/CollectionActions";
import { clientsActions } from "../store";
import TableShimmer from "../common/components/TableShimmer";
import { useDispatch, useSelector } from "react-redux";
import CollectionFab from "../settings/CollectionFab";
import { useNavigate } from "react-router-dom";
import ErrorDialog from "../common/components/ErrorDialog";

const ClientsPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("Error !!");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterProductCount, setFilterProductCount] = useState("all");
  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const _clients = useSelector((state) => state.clients.items);
  const _products = useSelector((state) => state.products.items);

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL_NAME}/clients`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        dispatch(clientsActions.setItems(data));
        return;
      }

      if (response.status === 401) {
        setErrorMsg("UNAUTHORIZED");
        setOpening(true);
        navigate("/login");
      } else {
        const text = await response.text();
        setErrorMsg(text);
        setOpening(true);
        throw Error(text);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpeningResult = () => setOpening(false);

  const getProductNames = (produits) => {
    if (!produits || produits.length === 0) return [];
    return produits.map((p) => {
      const prod = typeof p === "string" ? _products.find((pr) => pr._id === p) : p;
      return prod
        ? {
            designation: prod.designation || "N/A",
            reference: prod.reference || "N/A",
            marque: prod.marque || "N/A",
            type: prod.type || "N/A",
          }
        : null;
    }).filter(Boolean);
  };

  const getProductBadgeColor = (count) => {
    if (count === 0) return "default";
    if (count <= 3) return "primary";
    if (count <= 6) return "warning";
    return "error";
  };

  const filteredClients = _clients
    ?.filter((client) => {
      const keyword = searchKeyword.toLowerCase();
      if (!keyword) return true;
      const productMatch =
        client.produits &&
        client.produits.some((p) => {
          const prod = typeof p === "string" ? _products.find((pr) => pr._id === p) : p;
          if (!prod) return false;
          return (
            (prod.designation && prod.designation.toLowerCase().includes(keyword)) ||
            (prod.reference && prod.reference.toLowerCase().includes(keyword))
          );
        });
      return (
        client.username?.toLowerCase().includes(keyword) ||
        client.email?.toLowerCase().includes(keyword) ||
        client.phone?.toLowerCase().includes(keyword) ||
        client.adresse?.toLowerCase().includes(keyword) ||
        productMatch
      );
    })
    .filter((c) =>
      filterProductCount === "all"
        ? true
        : filterProductCount === "0"
        ? !c.produits || c.produits.length === 0
        : c.produits?.length >= parseInt(filterProductCount)
    );

  const paginatedClients = filteredClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleOpenPopup = (products) => {
    setSelectedProducts(products);
    setPopupOpen(true);
  };
  const handleClosePopup = () => setPopupOpen(false);

  return (
    <>
<Typography
  variant="h3"
  align="center"
  sx={{
    mb: { xs: 2, sm: 3 },
    fontWeight: 800,
    background: "linear-gradient(90deg, #002355, #0081f9)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
    textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
  }}
>        Clients
      </Typography>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Filtrer par nombre de produits</InputLabel>
          <Select
            value={filterProductCount}
            onChange={(e) => setFilterProductCount(e.target.value)}
            label="Filtrer par nombre de produits"
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="0">0 produit</MenuItem>
            <MenuItem value="1">≥ 1 produit</MenuItem>
            <MenuItem value="3">≥ 3 produits</MenuItem>
            <MenuItem value="5">≥ 5 produits</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
<TableRow
    sx={{
      backgroundColor: "#002355",
      color: "#fff",
      "& th": {
        color: "#fff",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 1,
        fontSize: { xs: 12, sm: 14 },
        borderBottom: "none",
      },
      "& th.actions": {
        color: "#0081f9",
      },
    }}
  >                   <TableCell>#</TableCell>
              <TableCell>NOM</TableCell>
              <TableCell>EMAIL</TableCell>
              <TableCell>TÉLÉPHONE</TableCell>
              <TableCell>ADRESSE</TableCell>
              <TableCell>PRODUITS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && paginatedClients?.length > 0 ? (
              paginatedClients.map((client, idx) => {
                const products = getProductNames(client.produits);
                return (
                  <TableRow
                    key={client._id}
                    sx={{
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      backgroundColor: idx % 2 === 0 ? "#fafafa" : "#fff",
                    }}
                  >
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>{client.username}</TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>{client.phone}</TableCell>
                    <TableCell>{client.adresse || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={products.length}
                        color={getProductBadgeColor(products.length)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      {products.length > 0 && (
                        <IconButton size="small" onClick={() => handleOpenPopup(products)}>
                          <VisibilityIcon />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell padding="none">
                      <CollectionActions
                        itemId={client._id}
                        editPath="/settings/client"
                        endpoint="clients"
                        action={clientsActions}
                        removable={true}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableShimmer columns={7} endAction />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredClients.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />

      <CollectionFab editPath="/settings/client" />

      <ErrorDialog
        style={{ transform: "none" }}
        open={opening}
        errorMsg={errorMsg}
        onResult={handleOpeningResult}
      />

      {/* 🔹 Popup pour liste des produits */}
      <Dialog open={popupOpen} onClose={handleClosePopup} maxWidth="sm" fullWidth>
        <DialogTitle>Liste des produits</DialogTitle>
        <DialogContent>
          {selectedProducts.length > 0 ? (
            <List>
              {selectedProducts.map((p, idx) => (
                <ListItem key={idx}>
                  <ListItemText
                    primary={`${p.designation} (${p.reference})`}
                    secondary={`Marque: ${p.marque} - Type: ${p.type}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography>Aucun produit</Typography>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientsPage;
